/**
 * Layout.js
 * The Skeleton Wrapper that holds the UI together.
 */
import { Header } from './Header.js';
import { BottomTab } from './BottomTab.js';
import { SystemMenuDrawer } from './SystemMenuDrawer.js';
import { Footer } from './Footer.js';

export class Layout {
    constructor(container) {
        this.container = container;

        // Create Layers
        this.uiLayer = document.createElement('div');
        this.uiLayer.id = "ui-layer";
        this.uiLayer.className = "absolute inset-0 pointer-events-none z-40";

        // Main Page Layer (Semantic <main>)
        // md:pl-[70px] for Sidebar space on Desktop (Collapsed Width)
        // [REF_FOOTER] Added flex flex-col to push footer to bottom
        this.pageLayer = document.createElement('main');
        this.pageLayer.className = "min-h-screen pb-16 md:pb-0 pt-14 md:pl-[70px] transition-all duration-300 flex flex-col relative";

        // [REF_FOOTER] Content Container (The Router clears THIS, not the pageLayer)
        // [FIX] Removed 'flex-col' from pageContent. Keeping it as 'flex-1' ensures it fills space, 
        // but internal flow remains standard 'block' for pages like Home/MyPage to render correctly.
        this.pageContent = document.createElement('div');
        this.pageContent.className = "flex-1 w-full relative";
        this.pageLayer.appendChild(this.pageContent);

        this.modalLayer = document.createElement('div');
        this.modalLayer.id = "modal-layer";
        this.modalLayer.className = "relative z-50";

        // Clear initial loading state
        this.container.innerHTML = '';
        this.container.appendChild(this.pageLayer);

        this.initUI();

        // Inject Global Style for Home/Viewer Route behavior (Robustness Fix)
        const style = document.createElement('style');
        style.innerHTML = `
            body.is-home-route #header-btn-search { display: none !important; }
            body.is-viewer-route #app-header { display: none !important; }
            body.is-studio-route #app-header { display: none !important; }
        `;
        document.head.appendChild(style);
    }

    initUI() {
        // System Menu Drawer (Adaptive: Mobile=Drawer, Desktop=Sidebar)
        this.drawer = new SystemMenuDrawer(document.body);

        // Header
        this.header = new Header(document.body, {
            onToggleDrawer: () => this.drawer.toggle()
        });

        // Bottom Tab
        this.bottomTab = new BottomTab(document.body);

        // Footer (Global)
        this.footer = new Footer();
        this.footer.render().then(el => {
            this.pageLayer.appendChild(el);
        });

        // Apply routing visibility logic
        this.handleRouteChanged();

        // Listen for route changes (Hash based)
        window.addEventListener('hashchange', () => {
            this.handleRouteChanged();
        });

        // Also listen to popstate just in case
        window.addEventListener('popstate', () => {
            this.handleRouteChanged();
        });

        // Custom Modal Listener
        window.addEventListener('open-modal', (e) => {
            alert(`Opening Modal: ${e.detail.label || e.detail.id} (Not implemented yet - Phase 2.2)`);
        });

        // Header Action Listener (Search & Notification)
        window.addEventListener('header-action', async (e) => {
            const action = e.detail;

            if (action === 'toggleNotification') {
                if (!window.user) {
                    alert('로그인이 필요한 서비스입니다.');
                    window.router.navigate('/login');
                    return;
                }
                const { NotificationListModal } = await import('../notification-list-modal.js');
                NotificationListModal.show(window.user.uid);
            }
            else if (action === 'toggleSearch') {
                const { SearchModal } = await import('../search-modal.js');
                SearchModal.show();
            }
        });

        // Scroll Logic for "Scroll to Hide/Show"
        this.lastScrollY = window.scrollY;
        this.ticking = false;

        window.addEventListener('scroll', () => {
            if (!this.ticking) {
                window.requestAnimationFrame(() => {
                    this.handleScroll();
                    this.ticking = false;
                });
                this.ticking = true;
            }
        });
    }

    handleScroll() {
        const currentScrollY = window.scrollY;
        const delta = currentScrollY - this.lastScrollY;
        const tabEl = document.getElementById('bottom-tab');

        // Threshold to prevent jitter
        if (Math.abs(delta) < 10) return;

        // Path Checks to ensure we don't show it when it should be permanently hidden (Login/Editor)
        const path = window.location.hash.slice(1) || '/';
        const cleanPath = path.split('?')[0];
        const isLogin = cleanPath === '/login';
        const isEditor = cleanPath.includes('/editor');

        // If it's a page where Tab should ALWAYS be hidden, do nothing (keep it hidden)
        // OR if it's Viewer, let ViewerHeader control it (Immersive Mode)
        const isViewer = cleanPath.includes('/episode/') || cleanPath.startsWith('/webnovel/');
        if (isLogin || isEditor) {
            this.lastScrollY = currentScrollY;
            return;
        }

        if (tabEl) {
            // Scroll Down -> Hide
            // Scroll Up -> Show
            // Boundary: Don't hide if near top (e.g. < 50px) to allow easy access
            if (delta > 0 && currentScrollY > 50) {
                tabEl.classList.add('translate-y-full');
            } else {
                tabEl.classList.remove('translate-y-full');
            }
        }

        this.lastScrollY = currentScrollY;
    }

    handleRouteChanged() {
        const path = window.location.hash.slice(1) || '/';
        const cleanPath = path.split('?')[0];

        // Route Definitions
        const isLogin = cleanPath === '/login';
        const isStudio = cleanPath.startsWith('/studio');
        const isEditor = cleanPath.includes('/editor'); // Studio Editor
        const isViewer = cleanPath.includes('/episode/') || cleanPath.startsWith('/webnovel/');
        const isSeriesDetail = cleanPath.startsWith('/series/') && !cleanPath.includes('/episode/');

        const headerEl = document.getElementById('app-header');
        const tabEl = document.getElementById('bottom-tab');

        // --- 0. Robust Body Class Management ---
        if (isStudio) {
            document.body.classList.add('is-studio-route');
        } else {
            document.body.classList.remove('is-studio-route');
        }

        if (isViewer) {
            document.body.classList.add('is-viewer-route');
        } else {
            document.body.classList.remove('is-viewer-route');
        }

        // 1. Header & Footer Visibility
        // Hide Main Header on: Login, Studio (has own header), Viewer (immersive), Editor
        const hideHeader = isLogin || isStudio || isViewer || isEditor;

        // Hide Footer on same pages + maybe others?
        // [FIX] User requested Footer on Viewers (Webtoon/Webnovel).
        // Hide on: Login, Studio (Own Layout), Editor (Fullscreen Workspace).
        const hideFooter = isLogin || (isStudio && !cleanPath.includes('/studio/dashboard')) || isEditor;

        if (this.footer && this.footer.element) {
            // Special Case: On Viewers, we might want to style it differently or just show it.
            // For now, simply SHOW it.
            if (hideFooter) {
                this.footer.element.style.display = 'none';
            } else {
                this.footer.element.style.display = 'block';
            }
        }

        if (headerEl) {
            if (hideHeader) {
                headerEl.classList.add('-translate-y-full');
            } else {
                headerEl.classList.remove('-translate-y-full');

                // Smart Search Icon Visibility (Hardened)
                const isHome = cleanPath === '/' || cleanPath === '';

                // Use setTimeout to ensure Header DOM is fully rendered
                setTimeout(() => {
                    const searchBtn = document.getElementById('header-btn-search');
                    if (searchBtn) {
                        searchBtn.style.display = isHome ? 'none' : 'block';
                    }
                }, 100);
            }
        }

        // 2. Tab Visibility
        // Hide Bottom Tab on: Login, Editor, Viewer (initially hidden, shown at EOF)
        const hideTab = isLogin || isEditor;

        if (tabEl) {
            if (hideTab) {
                tabEl.classList.add('translate-y-full');
            } else {
                // If we entered a page where it SHOULD be visible, show it initially!
                tabEl.classList.remove('translate-y-full');
            }
        }

        // 3. Page Padding (Prevent content overlap)
        // 3. Page Padding (Prevent content overlap)
        // Top Padding: Needed if Header is Visible AND NOT Full Bleed (SeriesDetail)
        if (!hideHeader && !isSeriesDetail) {
            this.pageLayer.classList.add('pt-14');
        } else {
            this.pageLayer.classList.remove('pt-14');
        }

        // [FIX] Sidebar Padding Logic
        // Normal: Add md:pl-[70px] for Sidebar
        // Studio/Login: Remove md:pl-[70px] for Full Screen (Immersive Mode) or Background Fill
        if (isStudio || isLogin) {
            this.pageLayer.classList.remove('md:pl-[70px]');
        } else {
            this.pageLayer.classList.add('md:pl-[70px]');
        }

        // Force Drawer Update (to reflect isStudio class change)
        if (this.drawer) this.drawer.updateDrawerState();
        // Padding Bottom: If Tab is allowed, add padding. 
        // Note: Even if we hide it via scroll, the padding reserves space so footer doesn't overlay content permanently.
        // It's better to keep the padding or the last item might be cut off.
        if (!hideTab) {
            this.pageLayer.classList.add('pb-16');
        } else {
            this.pageLayer.classList.remove('pb-16');
        }

        // 4. Update Header State (Reflect Auth Changes)
        if (this.header && this.header.updateHeader) {
            this.header.updateHeader();
        }
    }

    getPageContainer() {
        return this.pageContent; // [REF_FOOTER] Return the inner container for Router
    }
}
